import { Settings } from "@mui/icons-material";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import Hd from "@mui/icons-material/Hd";
import Sd from "@mui/icons-material/Sd";
import SlowMotionVideoIcon from "@mui/icons-material/SlowMotionVideo";
import { MenuItem, MenuList } from "@mui/material";
import { useEffect, useMemo, useState } from "react";

import { VideoResolution } from "../../../../__generated__/globalTypes";
import {
  ModuleFragment_videoSuffixes_mp4,
  ModuleFragment_videoSuffixes_webm,
} from "../../../../hooks/__generated__/ModuleFragment";

type VideoSuffixes = ModuleFragment_videoSuffixes_mp4 | ModuleFragment_videoSuffixes_webm;
import { useAppState } from "../../../../hooks/useAppState";
import { usePersistedSettings } from "../../../../hooks/usePersistedSettings";
import { createTelemetryEvent } from "../../../../telemetry/Telemetry";
import { speedModifier } from "../../../../util/constants";
import {
  ButtonActionsContainer,
  Container,
  SettingsButton,
  SettingsButtonContainer,
  StyledListItemIcon,
  StyledListItemText,
  StyledPaper,
} from "./ButtonSettings.elements";
import SettingsMenu from "./SettingsMenu";

type ButtonSettingsProps = {
  playbackRate: number;
  onClickPlaybackRate: (value: number) => void;
  onClickPIP: () => void;
  hasCustomDuration: boolean;
  className?: string;
};

export interface SettingsValue {
  display: string;
  value: string | number;
  autoResolutionText?: string;
}

export function calculateAutoResolutionText(
  videoQuality: VideoResolution | "auto",
  determineVideoQualityByWidth: (qualities: VideoSuffixes) => VideoResolution | undefined,
  mp4Qualities: VideoSuffixes,
): string {
  // Only show resolution text if user has selected "auto"
  if (videoQuality !== "auto") {
    return "";
  }

  const autoSelectedQuality = determineVideoQualityByWidth(mp4Qualities);
  if (!autoSelectedQuality) {
    return "";
  }

  const qualityToResolution: Record<VideoResolution, string> = {
    [VideoResolution.FHD]: "1080",
    [VideoResolution.HD]: "720",
    [VideoResolution.SD]: "480",
    [VideoResolution.VBR]: "VBR",
  };

  const resolution = qualityToResolution[autoSelectedQuality];
  if (resolution && resolution !== "VBR") {
    return `(${resolution}p)`;
  }

  return "";
}

export interface SettingsAction {
  icon: React.ReactElement;
  name: string;
  displayName: string;
  handler: (arg: SettingsValue) => void;
  id: string;
  ariaLabel: string;
  divider: boolean;
  values: SettingsValue[];
  selectedValue: string;
}

export const playbackRates = [
  {
    display: "0.7",
    value: 0.7,
  },
  {
    display: "1.0",
    value: 1.0,
  },
  {
    display: "1.2",
    value: 1.2,
  },
  {
    display: "1.5",
    value: 1.5,
  },
  {
    display: "2.0",
    value: 2.0,
  },
];

export default function ButtonSettings({
  playbackRate,
  onClickPlaybackRate,
  hasCustomDuration,
  className,
}: ButtonSettingsProps): React.ReactElement {
  const {
    setVideoQuality,
    appState: { videoQuality, module },
    determineVideoQualityByWidth,
  } = useAppState();
  const { quality } = usePersistedSettings();

  const videoQualities = useMemo(() => {
    if (!module?.videoSuffixes) return [{ display: "Auto", value: "auto" }];
    const availableQualities = [];
    const mp4Qualities = module.videoSuffixes.mp4;
    if (mp4Qualities?.FHD) {
      availableQualities.push({ display: "1080", value: "FHD" });
    }
    if (mp4Qualities?.HD) {
      availableQualities.push({ display: "720", value: "HD" });
    }
    if (mp4Qualities?.SD) {
      availableQualities.push({ display: "480", value: "SD" });
    }

    // Calculate auto-selected quality and show resolution only if user has selected "auto"
    const autoResolutionText = calculateAutoResolutionText(
      videoQuality || "auto",
      determineVideoQualityByWidth,
      mp4Qualities,
    );

    availableQualities.push({
      display: "Auto",
      value: "auto",
      autoResolutionText,
    });

    return availableQualities;
  }, [module?.videoSuffixes, determineVideoQualityByWidth, videoQuality]);

  const [showQualities, setShowQualities] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [selectedQuality, setSelectedQuality] = useState(
    videoQualities.find(qual => qual.value === quality)?.display || "Auto",
  );
  const [selectedPlayback, setSelectedPlayback] = useState(
    playbackRate?.toLocaleString("en-US", { minimumFractionDigits: 1 }),
  );
  const [selectedSetting, setSelectedSetting] = useState("");

  // Update selectedQuality when videoQualities changes (for auto display text)
  useEffect(() => {
    const currentQuality = videoQualities.find(qual => qual.value === quality);
    if (currentQuality) {
      setSelectedQuality(currentQuality.display);
    }
  }, [videoQualities, quality]);

  const handleShowSettings = (setting: string) => {
    setShowSettings(false);
    setShowQualities(true);
    setSelectedSetting(setting);
  };

  const handleChangeQuality = ({ value, display }: { value: string; display: string }) => {
    setSelectedQuality(display);
    setVideoQuality(value as VideoResolution);

    createTelemetryEvent({
      feature: "playback",
      action: "set quality",
      action_meta: value.toLowerCase(),
    }).fire("telemetry");

    setTimeout(() => {
      setShowQualities(false);
    }, 600);
  };

  const handlePlaybackChange = (playback: { value: number; display: string }) => {
    setSelectedPlayback(playback.display);
    onClickPlaybackRate(playback.value);

    setTimeout(() => {
      setShowQualities(false);
    }, 400);
  };

  const handleSettingsClick = () => {
    if (showQualities) {
      setShowQualities(false);
      setShowSettings(false);
      return;
    }
    setShowSettings(!showSettings);
  };

  const handleSettingsMenuBack = () => {
    setShowSettings(true);
    setShowQualities(false);
  };

  const actions = [
    {
      icon: <SlowMotionVideoIcon />,
      name: `${hasCustomDuration ? (playbackRate / speedModifier).toFixed(1) : playbackRate}x`,
      displayName: "Playback Rate",
      handler: handlePlaybackChange,
      id: "button_playback_speed",
      ariaLabel: `Playback speed ${playbackRate}X`,
      divider: true,
      values: playbackRates,
      selectedValue: selectedPlayback,
    } as SettingsAction,
    {
      icon: videoQuality === "SD" ? <Sd /> : <Hd />,
      name: "Quality",
      displayName: "Quality",
      handler: handleChangeQuality,
      id: "button_video_quality",
      ariaLabel: `Video quality, ${videoQuality}`,
      divider: false,
      values: videoQualities,
      selectedValue: selectedQuality,
    } as SettingsAction,
  ];

  const currentMenuValues = actions.filter(setting => setting.displayName === selectedSetting);

  return (
    <Container className={className}>
      <SettingsButtonContainer>
        <SettingsButton
          ariaLabel="Video settings"
          icon={<Settings />}
          FabProps={{ tabIndex: 1 }}
          onClick={() => handleSettingsClick()}
        />
        {showSettings && (
          <ButtonActionsContainer>
            <StyledPaper>
              <MenuList>
                {actions.map(action => (
                  <MenuItem
                    key={action.name}
                    onClick={() => handleShowSettings(action.displayName)}
                    divider={action.divider}
                    aria-label={action.ariaLabel}
                  >
                    <StyledListItemIcon>{action.icon}</StyledListItemIcon>
                    <StyledListItemText>{action.name}</StyledListItemText>
                    <ArrowForwardIosIcon sx={{ color: "white", fontSize: "0.75rem" }} />
                  </MenuItem>
                ))}
              </MenuList>
            </StyledPaper>
          </ButtonActionsContainer>
        )}
        {showQualities && <SettingsMenu setting={currentMenuValues} handleSettingsMenuBack={handleSettingsMenuBack} />}
      </SettingsButtonContainer>
    </Container>
  );
}
